import {
  arrayToEntityDict,
  courseOrganizationId,
  type CourseOrganization,
  type DeletableEntity,
  type Entity,
  type EntityDict,
  type Etag,
  type UserSlim
} from '@webapp2/shared/model'
import { capitalize, computed, toRaw, type ComputedRef } from 'vue'
import { postGetBody } from '@webapp2/shared/services/api'
import { adminCache } from '../../services/cache'
import { etagId, isString, type PermissionGroup } from '../../state'
import { adminState, type AdminCourse, type AdminState, type EntitySlim, type OrganizationSlim } from '../state'

let initialUpdateResolve: () => void

export const initialAdminUpdate = new Promise<void>(resolve => (initialUpdateResolve = resolve))

export async function setAdminStateFromCache() {
  const [courses, courseOrganizations, organizationSlims, permissionGroups, playerTechniqueSlims, userSlims] =
    await Promise.all([
      adminCache.getByType<AdminCourse>('Course'),
      adminCache.getByType<CourseOrganization>('CourseOrganization'),
      adminCache.getByType<OrganizationSlim>('OrganizationSlim'),
      adminCache.getByType<PermissionGroup>('PermissionGroup'),
      adminCache.getByType<EntitySlim>('PlayerTechniqueSlim'),
      adminCache.getByType<UserSlim>('UserSlim')
    ])

  adminState.courses = courses
  adminState.courseOrganizations = courseOrganizations
  adminState.organizationSlims = organizationSlims
  adminState.permissionGroups = permissionGroups
  adminState.playerTechniqueSlims = playerTechniqueSlims
  adminState.userSlims = userSlims
}

export function updateEntities(loadAll = false) {
  return updateByTypes(Object.keys(typeToStateKey) as AdminEntityType[], loadAll)
}

async function getByTypes(types: string[], loadAll: boolean) {
  const etags = await adminCache.getByType<Etag>('Etag')
  return await postGetBody<GetDocumentsResponse<EntityDict<Entity>>>(
    'admin/documents',
    types.map(x => {
      const etag = loadAll ? undefined : etags[etagId(x)]?.timestamp
      return { type: capitalize(x), etag }
    })
  )
}

export async function updateByTypes<K extends AdminEntityType>(types: K[], loadAll = false): Promise<void> {
  const response = await getByTypes(types, loadAll)
  if (!response) {
    console.log(`Failed to update via API using cache for type: ${types.join(', ')}`)
    initialUpdateResolve()
    return
  }

  for (const type of types) {
    const stateKey = typeToStateKey[type]
    const documents = response[type] ?? []
    const docs = arrayToEntityDict(documents) as EntityDict<DeletableEntity>

    if (loadAll) {
      adminState[stateKey] = docs as AdminState[typeof stateKey]
      void adminCache.deleteByType(type)
      await adminCache.setItems(Object.values(toRaw(adminState[stateKey])) as { id: string }[])
    } else {
      const deletedIds = Object.values(docs)
        .filter(x => x.deleted)
        .map(x => x.id)
      const merged: EntityDict<Entity> = { ...adminState[stateKey], ...docs }
      deletedIds.forEach(id => delete merged[id])
      adminState[stateKey] = merged as AdminState[typeof stateKey]
      await adminCache.setItems(Object.values(docs).filter(x => !x.deleted))
      await adminCache.deleteItems(deletedIds)
    }

    if (documents.length > 0) {
      const id = etagId(type)
      const timestamp = documents.reduce((acc, curr) => Math.max(acc, curr.timestamp ?? 0), 0)
      await adminCache.set(id, { id, type: 'Etag', entityType: type, timestamp })
    }
  }

  initialUpdateResolve()
}

export async function ensureById<T extends Entity>(type: string, store: EntityDict<T>, ids: string[]) {
  const idsToLoad = [...new Set(ids)].filter(x => !(x in store))
  if (idsToLoad.length === 0) {
    return
  }
  const response = await postGetBody<T[]>('admin/documents-by-id', { type, ids: idsToLoad })
  if (!response) {
    return
  }
  Object.assign(store, arrayToEntityDict(response))
}

export type GetDocumentsResponse<T extends Record<string, Entity>> = {
  [K in keyof T]?: T[K][]
}

export type AdminEntityType = keyof typeof typeToStateKey

const typeToStateKey = {
  Course: 'courses',
  CourseOrganization: 'courseOrganizations',
  OrganizationSlim: 'organizationSlims',
  PlayerTechniqueSlim: 'playerTechniqueSlims',
  PermissionGroup: 'permissionGroups'
} as const

function orderedEntitySlims<T extends EntitySlim>(slims: EntityDict<T>) {
  return Object.values(slims).sort((a, b) => a.name.localeCompare(b.name))
}

function entitySlimsToOptions(slims: ComputedRef<EntitySlim[]>) {
  return computed(() => slims.value.map(x => ({ label: x.name, value: x.id })))
}

export const techniques = computed(() => orderedEntitySlims(adminState.playerTechniqueSlims))
export const techniqueOptions = entitySlimsToOptions(techniques)
export const courses = computed(() => orderedEntitySlims(adminState.courses))
export const courseOptions = entitySlimsToOptions(courses)
export const organizations = computed(() => orderedEntitySlims(adminState.organizationSlims))
export const organizationOptions = entitySlimsToOptions(organizations)
export const organizationIdsByCourseId = computed(() =>
  Object.values(adminState.courseOrganizations).reduce<{ [courseId: string]: string[] }>((acc, courseOrganization) => {
    acc[courseOrganization.courseId] = [...(acc[courseOrganization.courseId] ?? []), courseOrganization.organizationId]
    return acc
  }, {})
)

export const organizationNamesByCourseId = computed(() =>
  Object.fromEntries(
    Object.entries(organizationIdsByCourseId.value).map(([courseId, organizationIds]) => [
      courseId,
      organizationIds
        .map(x => adminState.organizationSlims[x]?.name)
        .filter(isString)
        .sort((x, y) => x.localeCompare(y))
    ])
  )
)

export function sameTechniqueCoursesByOrganizationId(course: AdminCourse) {
  const sameTechniqueCourses = arrayToEntityDict(
    Object.values(adminState.courses).filter(x => x.techniqueId === course.techniqueId && x.id !== course.id)
  )
  return Object.values(adminState.courseOrganizations).reduce<{ [organizationId: string]: AdminCourse[] }>((acc, x) => {
    const sameTechniqueCourse = sameTechniqueCourses[x.courseId]
    if (sameTechniqueCourse) {
      acc[x.organizationId] = [...(acc[x.organizationId] ?? []), sameTechniqueCourse]
    }
    return acc
  }, {})
}

export function applyCourseOrganizations(course: AdminCourse, organizationIds: string[]) {
  const currentOrganizationIds = organizationIdsByCourseId.value[course.id] ?? []
  const newlyReleased = organizationIds.filter(x => !currentOrganizationIds.includes(x))
  const replacedByOrganizationId = sameTechniqueCoursesByOrganizationId(course)
  const replacedReleaseIds = new Set(
    newlyReleased.flatMap(organizationId =>
      (replacedByOrganizationId[organizationId] ?? []).map(x => courseOrganizationId(x.id, organizationId))
    )
  )
  const keptReleases = Object.values(adminState.courseOrganizations).filter(
    x => x.courseId !== course.id && !replacedReleaseIds.has(x.id)
  )
  const courseReleases = organizationIds.map<CourseOrganization>(x => ({
    id: courseOrganizationId(course.id, x),
    type: 'CourseOrganization',
    courseId: course.id,
    organizationId: x
  }))
  adminState.courseOrganizations = arrayToEntityDict([...keptReleases, ...courseReleases])
}
